import { Component, ElementRef, inject, output, signal, ViewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ValidationService } from '../../../core/common/utils/validation';

export interface DocumentFileEntry {
  name: string;
  uri: string;
}

@Component({
  selector: 'app-document-upload',
  standalone: true,
  imports: [
    MatIcon,
    MatButtonModule,
    MatButtonToggleModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
  ],
  templateUrl: './document-upload.html',
  styleUrl: './document-upload.scss',
})
export class DocumentUploadComponent {
  private readonly fb = inject(FormBuilder);

  @ViewChild('uriInput') uriInputRef?: ElementRef<HTMLInputElement>;

  /** Emittiert Einträge { name, uri } nach Submit */
  readonly filesSelected = output<DocumentFileEntry[]>();

  /** Visuelles Feedback: true wenn Dateien über die Drop-Zone gezogen werden */
  readonly isDragOver = signal(false);

  /** Grüne Hervorhebung nach erfolgreichem Drop */
  readonly dropSuccess = signal(false);

  readonly uriForm = this.fb.group({
    displayName: ['', Validators.required],
    uri: ['', [Validators.required]],
  });

  get nameControl() {
    return this.uriForm.get('displayName')!;
  }

  get uriControl() {
    return this.uriForm.get('uri')!;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.fillNameFromFile(files[0]);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.fillNameFromFile(input.files[0]);
      input.value = '';
    }
  }

  private fillNameFromFile(file: File): void {
    this.nameControl.setValue(file.name);
    this.nameControl.markAsTouched();
    this.dropSuccess.set(true);
    // Fokus auf URI-Feld setzen
    setTimeout(() => this.uriInputRef?.nativeElement.focus(), 0);
  }

  onSubmit(): void {
    this.uriForm.markAllAsTouched();
    if (this.uriForm.invalid) return;

    const name = this.nameControl.value?.trim() ?? '';
    const uri = this.uriControl.value?.trim() ?? '';

    this.filesSelected.emit([{ name, uri }]);

    // Formular zurücksetzen
    this.uriForm.reset();
    this.uriForm.markAsPristine();
    this.uriForm.markAsUntouched();
    this.dropSuccess.set(false);
    this.uriControl.clearValidators();
    this.uriControl.setValidators([Validators.required, ValidationService.urlValidator()]);
    this.uriControl.updateValueAndValidity();
  }
}
